<template>
  <div class="search-filters">
    <animated-dropdown v-if="isMobile"
                       v-model="selectedValues"
                       :label="capitalize( $attrs.name )"
                       :options="filteredOptions"
                       @update="(selectedFilters) => updateValue( selectedFilters )">
      <template #header>
        <div class="search-filter-tags__header">
          <font-awesome-icon class="search-filter-tags__header-icon"
                             :icon="['fal', 'filter']">
          </font-awesome-icon>
          <span>
            {{ $attrs.name }}
          </span>
        </div>
      </template>
      <template #no-options>
        test
      </template>
    </animated-dropdown>
    <div v-else
         class="search-filter-tags">
      <div class="search-filter-tags__header">
        <font-awesome-icon class="search-filter-tags__header-icon"
                           :icon="['fal', 'filter']">
        </font-awesome-icon>
        <span>
          {{ $attrs.name }}
        </span>
      </div>
      <transition-group
        name="checkbox-list"
        tag="ul"
        @enter="enter"
        @leave="leave">
        <li
          v-for="( option, index ) in filteredOptions"
          :key="option.key"
          :data-index="index">
          <input
            v-model="selectedValues"
            v-uni-id="option.key"
            :value="option.key"
            type="checkbox"
            @change="updateValue()">
          <label v-uni-for="option.key">
            <span class="fv-label">{{ option.value }}</span>
            <span
              v-if="!hideCounts"
              class="fv-count">
              ({{ option.count }})
            </span>
          </label>
        </li>

        <li
          key="no"
          :data-index="0">
          <span
            v-if="formattedOptions.length === 0"
            class="no-filter-options">
            No applicable options
          </span>
        </li>

        <li
          key="toggleList"
          :data-index="0">
          <span
            v-if="formattedOptions.length > majorHitsCount"
            class="more-filter-options"
            @click="showOnlyMajorHits = !showOnlyMajorHits"
            v-text="showOnlyMajorHits ? 'Show more' : 'Show less'">
          </span>
        </li>
      </transition-group>
    </div>
  </div>
</template>

<script>
import { TweenMax } from "gsap";
import AnimatedDropdown from "~/Components/Base/AnimatedDropdown.vue";
import { IsMobileMixin } from "~/Scripts/mixins/common";

export default {
  name: "TagCheckboxes",
  components: {
    AnimatedDropdown
  },
  mixins: [
    IsMobileMixin
  ],
  props: {
    value: {
      type: [String, Array],
      required: true
    },
    group: {
      type: String,
      required: true
    },
    filter: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      required: false,
      default: () => []
    },
    config: {
      type: Object,
      required: false,
      default: () => {}
    },
    noVuex: {
      type: Boolean,
      default: false
    },
    hideCounts: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedValues: [],
      showOnlyMajorHits: true,
      majorHitsCount: 5
    };
  },
  computed: {
    filteredOptions () {
      let result = this.formattedOptions;

      if ( this.showOnlyMajorHits ) {
        result = result.slice( 0, this.majorHitsCount );

        const enabledFilters = this.formattedOptions.filter(
          x => this.selectedValues.some( y => y === x.value )
            && !result.find( y => y.value === x.value )
        );

        result.push( ...enabledFilters );
      }

      return result;
    },
    formattedOptions () {
      const { options } = this;
      const results = [];
      let explicitKeys = false;

      if ( options != null && Array.isArray( options ) ) {
        // if all options contain "=" then we have explicit keys
        if ( options.every( ( x ) => x.value.indexOf( "=" ) >= 0 ) ) {
          explicitKeys = true;
        }

        // iterate through store options
        options.forEach( ( option, idx ) => {
          // if we have a valid option, format it and add it to our array
          if ( option.value ) {
            let key = option.value;
            let { value } = option;
            const { count } = option;

            if ( explicitKeys ) {
              const splitOption = option.value.split( "=" );
              [key = 0, value = 1] = splitOption;
            }

            const hideEmptyOptions = this.config.hideEmptyOptions || false;
            const optionSelected = this.selectedValues.some( x => x === option.value );
            const shouldShowOption = ( option.count > 0 || optionSelected );

            if ( !hideEmptyOptions || ( hideEmptyOptions && shouldShowOption ) ) {
              const label = this.hideCounts ? value : `${value} (${count})`;
              results.push( {
                key, value, count, label, id: idx
              } );
            }
          }
        } );
      }

      // sort by name ascending
      return results.sort( ( a, b ) => a.value.localeCompare( b.value ) );
    }
  },
  watch: {
    value: {
      immediate: true,
      handler ( newValue ) {
        if ( newValue ) {
          if ( typeof newValue === "string" ) {
            this.selectedValues = newValue.split( "," );
          } else {
            this.selectedValues = [...newValue];
          }
        } else {
          this.selectedValues = [];
        }
      }
    }
  },
  methods: {
    updateValue ( selectedFilters ) {
      if ( this.noVuex ) {
        this.$emit( "input", this.selectedValues );
      } else {
        const filteredNames = ( selectedFilters ?? this.selectedValues ).filter(
          ( n ) => n.trim() !== ""
        );

        const value = filteredNames.join( "," );
        const payload = {};

        payload.filter = this.filter;
        payload.group = this.group;
        payload.value = value;

        this.$store.dispatch( "applyFilter", payload );
      }
    },
    getTransformOrder ( transforms, currentItem ) {
      let result = 100;

      const transform = transforms.find( x => x.key === currentItem.key );
      if ( transform ) {
        result = transforms.indexOf( transform );
      }

      return result;
    },
    enter ( el, done ) {
      const delay = ( el.dataset.index * 0.01 );

      el.style.opacity = 0;
      TweenMax.killTweensOf( el );

      TweenMax.to(
        el,
        0.1,
        { opacity: 1, delay, onComplete: done }
      );
    },
    leave ( el, done ) {
      TweenMax.killTweensOf( el );

      TweenMax.to(
        el,
        0.2,
        { opacity: 0, onComplete: done }
      );
    },
    capitalize ( input ) {
      return input.charAt( 0 ).toUpperCase() + input.slice( 1 );
    }
  }
};
</script>

<style lang="less" scoped>
.search-filter-tags {
  margin: 0 -10px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  .more-filter-options {
    text-decoration: underline;
  }

  .no-filter-options,
  .more-filter-options {
    display: block;
    text-align: center;
  }

  .more-filter-options {
    margin-top: 10px;
    cursor: pointer;
  }

  @x-padding: 0.5rem;
  @checkbox-text-spacing: 40px;

  &__header {
    display: flex;
    color: #0C499C;
    font-size: 1.125rem;
    padding: 0.75rem calc( @x-padding + @checkbox-text-spacing );
    font-weight: 700;
    position: relative;
    border-bottom: 1px solid #0C499C;
  }

  &__header-icon {
    position: absolute;
    top: 1rem;
    left: 1rem;
  }

  ul {
    list-style-type: none;
    background-color: rgba(12, 73, 156, 0.05);
    padding: 1rem @x-padding 1.5rem;

    li {
      position: relative;
      cursor: pointer;

      input {
        &[type="checkbox"] {
          position: absolute;
          left: 10px;
          top: 9px;
          width: 16px;
          height: 16px;
        }

        &:checked {
          color: #333;
          background-color: #fff;
        }
      }

      label {
        font-weight: normal;
        display: block;
        margin: 0;
        padding: 5px 0 5px @checkbox-text-spacing;
        cursor: pointer;
        font-size: 12pt;
      }

      input:checked + label {
        color: #027A7E;
      }

      &:hover {
        label {
          color: #555;
          background: #eee;
        }
      }
    }
  }
}
</style>
